/**
 *
 * SetView
 *
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { isFuture, subMinutes, format } from 'utils/date-fns/index.js';
import { Card } from 'react-bootstrap';
import TwoColumns from 'components/layout/TwoColumns/index.js';
import Icon from 'components/media/Icon/index.js';
import notReady from 'utils/notReady.js';
import cx from 'utils/cx.js';
import DraftControls from './DraftControls.jsx';
import RegisterDate from './RegisterDate.jsx';
import messages from './messages.js';
import CompleteControls from './CompleteControls.jsx';
import useDeleteMeetings from '../hooks/useDeleteMeetings.js';

const Info = styled.div`
  line-height: 1.5;  
  h5 {
    font-weight: 400;
    margin: 0 0 5px;
  }
  div {
    color: ${(p) => p.theme.gray};
    .formatted-date, .date-past, .date-none {
      font-weight: 500;
      color: ${(p) => p.theme.textColor};
      padding-left: 0.5rem;
    }
    &.reg-start .date-past {
      color: ${(p) => p.theme.brandSuccess};
    }
    &.reg-end .date-past {
      color: ${(p) => p.theme.brandDanger};
    }
  }
`;

function SetView(props) {
  const { set, link, overviewLink } = props;
  const {
    title, id, start, end, timeStep, complete, blocks, registerStart, registerEnd, registerClose,
  } = set;
  const [deleted, setDeleted] = useState(false);
  const { deleteMeetingSet } = useDeleteMeetings();
  if (notReady(set)) {
    return null;
  }

  function handleDelete(body) {
    setDeleted(true);
    deleteMeetingSet(id, { body });
  }

  return (
    <Card className={cx('border', { opaque: deleted })}>
      <Card.Header className="border-bottom d-flex align-items-baseline">
        <h3 className="flex-grow-1">{title}</h3>
        {complete
          ? <CompleteControls link={link} set={set} onDelete={handleDelete} overviewLink={overviewLink} />
          : <DraftControls link={link} set={set} onDelete={handleDelete} />}
      </Card.Header>
      <Card.Body>
        <TwoColumns responsive>
          <div className="d-flex mb-4 mb-sm-0">
            <Icon bsSize="xlarge" className="ss-checkclipboard text-primary me-4" />
            <Info>
              <h5><FormattedMessage {...messages.registrationPeriod} /></h5>
              {registerStart
                ? (
                  <>
                    {(!registerClose || isFuture(registerClose))
                    && (
                      <div className="reg-start">
                        <FormattedMessage {...messages.startRegistration} />
                        <RegisterDate date={registerStart} msgPast={messages.started} />
                      </div>
                    )}
                    <div className="reg-end">
                      <FormattedMessage {...messages.endRegistration} />
                      <RegisterDate
                        date={registerClose}
                        open={!registerEnd}
                        msgPast={messages.closed}
                        msgNone={messages.openEnd}
                      />
                    </div>
                  </>
                )
                : <div><FormattedMessage {...messages.notSet} /></div>}
            </Info>
          </div>
          <div className="d-flex">
            <Icon bsSize="xlarge" className="ss-chat text-primary me-4" />
            <Info>
              <h5><FormattedMessage {...messages.meetingsPeriod} /></h5>
              {blocks && blocks.list.length > 0
                ? (
                  <>
                    <div>
                      <FormattedMessage {...messages.firstMeeting} />
                      <span className="formatted-date">{format(start, 'PPPp')}</span>
                    </div>
                    <div>
                      <FormattedMessage {...messages.lastMeeting} />
                      <span className="formatted-date">{format(subMinutes(end, timeStep), 'PPPp')}</span>
                    </div>
                  </>
                )
                : <div><FormattedMessage {...messages.notSet} /></div>}
            </Info>
          </div>
        </TwoColumns>
      </Card.Body>
    </Card>
  );
}

SetView.propTypes = {
  set: PropTypes.object,
  link: PropTypes.string,
  overviewLink: PropTypes.string,
};

export default SetView;
